export type EvaluationStatus = 'pending' | 'processing' | 'completed' | 'error';

/** Raw JSON from RawTaskLLMScores.NormedLLMResponce */
export interface NormedAiResponse {
  greeting?: { quality_score: number; present: boolean };
  farewell?: { quality_score: number; present: boolean };
  products?: Record<
    string,
    { mentioned: boolean; visit_quality_score: number }
  >;
  agent_comment?: string;
  visit_stages?: Record<string, unknown>;
  overall_score_percent?: number;
  [key: string]: unknown;
}

export type CriterionType = 'stage' | 'product' | 'visit_stage';
export type CriterionStatus = 'done' | 'partial' | 'not_done';

const MAX_SCORE = 10;

export interface AIEvaluationScore {
  name: string;
  type: CriterionType;
  score: number;
  maxScore: number;
  status: CriterionStatus;
  present: boolean;
  comment: string;
}

export interface AIEvaluationSummary {
  stagePercent: number;
  productQualityPercent: number;
  productCoverage: { mentioned: number; total: number };
  totalPercent: number;
}

export interface AIEvaluationStageGroup {
  name: string;
  score: number;
  maxScore: number;
  comment?: string;
  criteria: AIEvaluationScore[];
}

export interface AIEvaluation {
  taskId: number;
  status: EvaluationStatus;
  totalScorePercent: number;
  summary: string;
  scores: AIEvaluationScore[];
  stageGroups: AIEvaluationStageGroup[];
  breakdown: AIEvaluationSummary;
}

function scoreToStatus(score: number, maxScore: number): CriterionStatus {
  const pct = maxScore > 0 ? score / maxScore : 0;
  if (pct >= 0.8) return 'done';
  if (pct >= 0.4) return 'partial';
  return 'not_done';
}

export function statusLabel(status: CriterionStatus): string {
  switch (status) {
    case 'done':
      return 'Выполнено';
    case 'partial':
      return 'Частично';
    case 'not_done':
      return 'Не выполнено';
  }
}

const STAGE_KEYS: [string, string][] = [
  ['greeting', 'Приветствие'],
  ['farewell', 'Завершение']
];

function toCriterionScore(
  name: string,
  score: number,
  maxScore = MAX_SCORE,
  type: CriterionType = 'visit_stage',
  present = true,
  comment = ''
): AIEvaluationScore {
  return {
    name,
    type,
    score,
    maxScore,
    status: scoreToStatus(score, maxScore),
    present,
    comment
  };
}

function extractStageGroupsFromDynamicResponse(
  raw: NormedAiResponse
): AIEvaluationStageGroup[] {
  const stageRoot = raw.visit_stages;
  if (!stageRoot || typeof stageRoot !== 'object') return [];

  const groups: AIEvaluationStageGroup[] = [];
  for (const [stageName, stageValue] of Object.entries(stageRoot)) {
    if (!stageValue || typeof stageValue !== 'object') continue;
    const stageObj = stageValue as Record<string, unknown>;
    const criteriaSource =
      stageObj['criteria'] && typeof stageObj['criteria'] === 'object'
        ? (stageObj['criteria'] as Record<string, unknown>)
        : stageObj;

    const criteria: AIEvaluationScore[] = [];
    for (const [criterionName, criterionValue] of Object.entries(
      criteriaSource
    )) {
      if (!criterionValue || typeof criterionValue !== 'object') continue;
      const criterionObj = criterionValue as Record<string, unknown>;
      const score =
        typeof criterionObj['score'] === 'number'
          ? criterionObj['score']
          : typeof criterionObj['quality_score'] === 'number'
            ? criterionObj['quality_score']
            : null;
      if (score == null) continue;

      const maxScore =
        typeof criterionObj['max_score'] === 'number'
          ? criterionObj['max_score']
          : MAX_SCORE;
      const present =
        typeof criterionObj['present'] === 'boolean'
          ? criterionObj['present']
          : true;
      const comment =
        typeof criterionObj['comment'] === 'string'
          ? criterionObj['comment']
          : '';

      criteria.push(
        toCriterionScore(
          criterionName,
          score,
          maxScore,
          'visit_stage',
          present,
          comment
        )
      );
    }

    const declaredScore = stageObj['score'];
    const declaredMax = stageObj['max_score'];
    const avgScore = criteria.length
      ? Math.round(
          criteria.reduce((acc, c) => acc + c.score, 0) / criteria.length
        )
      : 0;

    groups.push({
      name: stageName,
      score: typeof declaredScore === 'number' ? declaredScore : avgScore,
      maxScore: typeof declaredMax === 'number' ? declaredMax : MAX_SCORE,
      comment:
        typeof stageObj['comment'] === 'string' ? stageObj['comment'] : '',
      criteria
    });
  }

  return groups;
}

function avgPercent(scores: number[], maxScore: number): number {
  if (scores.length === 0) return 0;
  const avg = scores.reduce((a, b) => a + b, 0) / scores.length;
  return Math.round((avg / maxScore) * 100);
}

export function parseNormedResponse(
  taskId: number,
  raw: NormedAiResponse
): AIEvaluation {
  const scores: AIEvaluationScore[] = [];
  const stageGroups = extractStageGroupsFromDynamicResponse(raw);

  for (const [key, label] of STAGE_KEYS) {
    const stage = raw[key as keyof NormedAiResponse] as
      | { quality_score: number; present: boolean }
      | undefined;
    if (stage && typeof stage.quality_score === 'number') {
      scores.push(
        toCriterionScore(
          label,
          stage.quality_score,
          MAX_SCORE,
          'stage',
          stage.present,
          ''
        )
      );
    }
  }

  if (raw.products) {
    for (const [brandName, product] of Object.entries(raw.products)) {
      scores.push(
        toCriterionScore(
          brandName,
          product.visit_quality_score,
          MAX_SCORE,
          'product',
          product.mentioned,
          product.mentioned ? '' : 'Не упомянут на визите'
        )
      );
    }
  }

  const stageScores = scores
    .filter((s) => s.type === 'stage')
    .map((s) => s.score);
  const productScores = scores.filter((s) => s.type === 'product');
  const mentionedProducts = productScores.filter((s) => s.present);
  const totalProducts = productScores.length;

  const stagePercent = stageGroups.length
    ? Math.round(
        (stageGroups.reduce((acc, g) => acc + g.score, 0) /
          stageGroups.reduce((acc, g) => acc + g.maxScore, 0)) *
          100
      )
    : avgPercent(stageScores, MAX_SCORE);

  const productQualityPercent = avgPercent(
    mentionedProducts.map((s) => s.score),
    MAX_SCORE
  );
  const coveragePercent =
    totalProducts > 0
      ? Math.round((mentionedProducts.length / totalProducts) * 100)
      : 0;

  const fallbackTotal = stageGroups.length
    ? stagePercent
    : Math.round(
        stagePercent * 0.3 + productQualityPercent * 0.4 + coveragePercent * 0.3
      );

  const rawOverall =
    typeof raw.overall_score_percent === 'number' &&
    Number.isFinite(raw.overall_score_percent)
      ? Math.min(100, Math.max(0, Math.round(raw.overall_score_percent)))
      : null;

  const totalPercent =
    rawOverall ?? (stageGroups.length ? stagePercent : fallbackTotal);

  const breakdown: AIEvaluationSummary = {
    stagePercent,
    productQualityPercent,
    productCoverage: {
      mentioned: mentionedProducts.length,
      total: totalProducts
    },
    totalPercent
  };

  if (!stageGroups.length && stageScores.length) {
    stageGroups.push({
      name: 'Этапы визита',
      score: Math.round(
        stageScores.reduce((acc, current) => acc + current, 0) /
          stageScores.length
      ),
      maxScore: MAX_SCORE,
      criteria: scores.filter((s) => s.type === 'stage')
    });
  }

  return {
    taskId,
    status: 'completed',
    totalScorePercent: totalPercent,
    summary: raw.agent_comment ?? '',
    scores,
    stageGroups,
    breakdown
  };
}
